/**
 * Biome table.
 *
 * Biomes are selected from a 4-dimensional climate space (temperature,
 * humidity, continentalness, erosion) rather than from a single Voronoi noise,
 * so that deserts sit next to savannas and snowy taiga sits next to snowy
 * plains, and so that oceans/beaches/rivers fall out of continentalness rather
 * than being painted on afterwards.
 */

export type TreeKind =
  | 'none' | 'oak' | 'big_oak' | 'birch' | 'spruce' | 'tall_spruce' | 'jungle'
  | 'big_jungle' | 'acacia' | 'dark_oak' | 'swamp_oak' | 'cactus' | 'dead_bush'
  | 'mushroom';

export interface BiomeDef {
  id: number;
  name: string;
  /** 0..1; below 0.15 water freezes and rain falls as snow. */
  temperature: number;
  /** 0..1 rainfall, drives grass/foliage colour and vegetation density. */
  downfall: number;
  grassColor: number;
  foliageColor: number;
  waterColor: number;
  skyColor: number;
  fogColor: number;
  surfaceTop: string;
  surfaceFiller: string;
  /** Block used where the surface is below sea level. */
  underwater: string;
  /** Stone-level block, for badlands-style banding or nether variants. */
  stone?: string;
  /** Average trees per chunk (can be fractional). */
  trees: number;
  treeKinds: { kind: TreeKind; weight: number }[];
  /** Average grass/fern patches per chunk. */
  grass: number;
  flowers: number;
  /** Extra features. */
  cactus?: number;
  sugarCane?: number;
  pumpkin?: number;
  melon?: number;
  deadBush?: number;
  lilyPad?: number;
  mushroom?: number;
  /** Height offset and variation hints applied on top of the terrain shaper. */
  heightBias?: number;
  heightScale?: number;
  /** Mob spawn categories allowed here. */
  passiveMobs?: { type: string; weight: number }[];
  /** Rare biome that should not be picked as a spawn point. */
  hostile?: boolean;
  dimension?: 'overworld' | 'nether' | 'the_end';
}

const biomes: BiomeDef[] = [];
const biomeByName = new Map<string, BiomeDef>();

function biome(name: string, o: Omit<BiomeDef, 'id' | 'name'>): BiomeDef {
  const b: BiomeDef = { id: biomes.length, name, ...o };
  biomes.push(b);
  biomeByName.set(name, b);
  return b;
}

const COW = { type: 'cow', weight: 8 };
const PIG = { type: 'pig', weight: 8 };
const SHEEP = { type: 'sheep', weight: 10 };
const CHICKEN = { type: 'chicken', weight: 8 };
const TEMPERATE_MOBS = [COW, PIG, SHEEP, CHICKEN];

biome('ocean', {
  temperature: 0.5, downfall: 0.5,
  grassColor: 0x8eb971, foliageColor: 0x71a74d, waterColor: 0x3f76e4,
  skyColor: 0x78a7ff, fogColor: 0xc0d8ff,
  surfaceTop: 'gravel', surfaceFiller: 'gravel', underwater: 'gravel',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  heightBias: -22, heightScale: 0.4,
});
biome('deep_ocean', {
  temperature: 0.5, downfall: 0.5,
  grassColor: 0x8eb971, foliageColor: 0x71a74d, waterColor: 0x3d57d6,
  skyColor: 0x78a7ff, fogColor: 0xc0d8ff,
  surfaceTop: 'gravel', surfaceFiller: 'gravel', underwater: 'gravel',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  heightBias: -38, heightScale: 0.3,
});
biome('frozen_ocean', {
  temperature: 0.0, downfall: 0.5,
  grassColor: 0x80b497, foliageColor: 0x60a17b, waterColor: 0x3938c9,
  skyColor: 0x7fa1ff, fogColor: 0xd0e0ff,
  surfaceTop: 'gravel', surfaceFiller: 'gravel', underwater: 'gravel',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  heightBias: -22, heightScale: 0.4,
});
biome('river', {
  temperature: 0.5, downfall: 0.5,
  grassColor: 0x8eb971, foliageColor: 0x71a74d, waterColor: 0x3f76e4,
  skyColor: 0x78a7ff, fogColor: 0xc0d8ff,
  surfaceTop: 'sand', surfaceFiller: 'sand', underwater: 'sand',
  trees: 0, treeKinds: [], grass: 1, flowers: 0,
  sugarCane: 2,
  heightBias: -7, heightScale: 0.15,
});
biome('beach', {
  temperature: 0.8, downfall: 0.4,
  grassColor: 0x91bd59, foliageColor: 0x77ab2f, waterColor: 0x3f76e4,
  skyColor: 0x78a7ff, fogColor: 0xc0d8ff,
  surfaceTop: 'sand', surfaceFiller: 'sand', underwater: 'sand',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  sugarCane: 1,
  heightBias: 1, heightScale: 0.12,
});
biome('stony_shore', {
  temperature: 0.2, downfall: 0.3,
  grassColor: 0x8ab689, foliageColor: 0x6da36b, waterColor: 0x3f76e4,
  skyColor: 0x78a7ff, fogColor: 0xc0d8ff,
  surfaceTop: 'stone', surfaceFiller: 'stone', underwater: 'stone',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  heightBias: 2, heightScale: 0.5,
});
biome('plains', {
  temperature: 0.8, downfall: 0.4,
  grassColor: 0x91bd59, foliageColor: 0x77ab2f, waterColor: 0x3f76e4,
  skyColor: 0x78a7ff, fogColor: 0xc0d8ff,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 0.5, treeKinds: [{ kind: 'oak', weight: 9 }, { kind: 'big_oak', weight: 1 }],
  grass: 14, flowers: 4, pumpkin: 0.1,
  heightBias: 0, heightScale: 0.6,
  passiveMobs: TEMPERATE_MOBS,
});
biome('sunflower_plains', {
  temperature: 0.8, downfall: 0.4,
  grassColor: 0x91bd59, foliageColor: 0x77ab2f, waterColor: 0x3f76e4,
  skyColor: 0x78a7ff, fogColor: 0xc0d8ff,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 0.3, treeKinds: [{ kind: 'oak', weight: 1 }],
  grass: 18, flowers: 12,
  heightBias: 0, heightScale: 0.6,
  passiveMobs: TEMPERATE_MOBS,
});
biome('meadow', {
  temperature: 0.5, downfall: 0.8,
  grassColor: 0x83bb6d, foliageColor: 0x63a948, waterColor: 0x0e4ecf,
  skyColor: 0x7ba4ff, fogColor: 0xc0d8ff,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 0.1, treeKinds: [{ kind: 'oak', weight: 1 }],
  grass: 20, flowers: 16,
  heightBias: 12, heightScale: 0.8,
  passiveMobs: [SHEEP, { type: 'cow', weight: 4 }],
});
biome('forest', {
  temperature: 0.7, downfall: 0.8,
  grassColor: 0x79c05a, foliageColor: 0x59ae30, waterColor: 0x3f76e4,
  skyColor: 0x79a6ff, fogColor: 0xc0d8ff,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 9, treeKinds: [{ kind: 'oak', weight: 8 }, { kind: 'big_oak', weight: 2 }, { kind: 'birch', weight: 2 }],
  grass: 6, flowers: 3, mushroom: 0.5,
  heightBias: 1, heightScale: 0.7,
  passiveMobs: TEMPERATE_MOBS,
});
biome('birch_forest', {
  temperature: 0.6, downfall: 0.6,
  grassColor: 0x88bb67, foliageColor: 0x6ba941, waterColor: 0x3f76e4,
  skyColor: 0x7aa5ff, fogColor: 0xc0d8ff,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 9, treeKinds: [{ kind: 'birch', weight: 1 }],
  grass: 5, flowers: 3,
  heightBias: 1, heightScale: 0.7,
  passiveMobs: TEMPERATE_MOBS,
});
biome('dark_forest', {
  temperature: 0.7, downfall: 0.8,
  grassColor: 0x507a32, foliageColor: 0x3f6b1f, waterColor: 0x3f76e4,
  skyColor: 0x79a6ff, fogColor: 0xa8bcd8,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 14, treeKinds: [{ kind: 'dark_oak', weight: 7 }, { kind: 'oak', weight: 3 }],
  grass: 3, flowers: 1,
  heightBias: 1, heightScale: 0.6,
  passiveMobs: TEMPERATE_MOBS,
});
biome('taiga', {
  temperature: 0.25, downfall: 0.8,
  grassColor: 0x86b783, foliageColor: 0x68a464, waterColor: 0x287082,
  skyColor: 0x7da2ff, fogColor: 0xc0d8ff,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 8, treeKinds: [{ kind: 'spruce', weight: 7 }, { kind: 'tall_spruce', weight: 3 }],
  grass: 8, flowers: 1, mushroom: 1,
  heightBias: 3, heightScale: 0.8,
  passiveMobs: [...TEMPERATE_MOBS],
});
biome('snowy_taiga', {
  temperature: 0.05, downfall: 0.4,
  grassColor: 0x80b497, foliageColor: 0x60a17b, waterColor: 0x205e83,
  skyColor: 0x84a9ff, fogColor: 0xd8e4ff,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 7, treeKinds: [{ kind: 'spruce', weight: 1 }],
  grass: 3, flowers: 0,
  heightBias: 3, heightScale: 0.8,
  passiveMobs: [SHEEP],
});
biome('snowy_plains', {
  temperature: 0.0, downfall: 0.5,
  grassColor: 0x80b497, foliageColor: 0x60a17b, waterColor: 0x3938c9,
  skyColor: 0x84a9ff, fogColor: 0xd8e4ff,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 0.05, treeKinds: [{ kind: 'spruce', weight: 1 }],
  grass: 1, flowers: 0,
  heightBias: 0, heightScale: 0.5,
  passiveMobs: [SHEEP],
});
biome('windswept_hills', {
  temperature: 0.2, downfall: 0.3,
  grassColor: 0x8ab689, foliageColor: 0x6da36b, waterColor: 0x007bf7,
  skyColor: 0x7da2ff, fogColor: 0xc0d8ff,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 1, treeKinds: [{ kind: 'spruce', weight: 2 }, { kind: 'oak', weight: 1 }],
  grass: 5, flowers: 1,
  heightBias: 26, heightScale: 1.6,
  passiveMobs: [SHEEP],
});
biome('jagged_peaks', {
  temperature: 0.0, downfall: 0.9,
  grassColor: 0x80b497, foliageColor: 0x60a17b, waterColor: 0x0e4ecf,
  skyColor: 0x87abff, fogColor: 0xdce8ff,
  surfaceTop: 'snow_block', surfaceFiller: 'stone', underwater: 'stone',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  heightBias: 62, heightScale: 2.4,
});
biome('savanna', {
  temperature: 1.2, downfall: 0.0,
  grassColor: 0xbfb755, foliageColor: 0xaea42a, waterColor: 0x3f76e4,
  skyColor: 0x6eb1ff, fogColor: 0xd8d0b0,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 1.2, treeKinds: [{ kind: 'acacia', weight: 4 }, { kind: 'oak', weight: 1 }],
  grass: 20, flowers: 1,
  heightBias: 1, heightScale: 0.5,
  passiveMobs: [{ type: 'cow', weight: 8 }, SHEEP, { type: 'chicken', weight: 4 }],
});
biome('desert', {
  temperature: 2.0, downfall: 0.0,
  grassColor: 0xbfb755, foliageColor: 0xaea42a, waterColor: 0x32a598,
  skyColor: 0x6eb1ff, fogColor: 0xe8dcb0,
  surfaceTop: 'sand', surfaceFiller: 'sand', underwater: 'sand',
  stone: 'sandstone',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  cactus: 3, deadBush: 2,
  heightBias: 2, heightScale: 0.5,
});
biome('badlands', {
  temperature: 2.0, downfall: 0.0,
  grassColor: 0x90814d, foliageColor: 0x9e814d, waterColor: 0x4e7f81,
  skyColor: 0x6eb1ff, fogColor: 0xe0c0a0,
  surfaceTop: 'red_sand', surfaceFiller: 'red_sandstone', underwater: 'red_sand',
  stone: 'terracotta',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  cactus: 1, deadBush: 3,
  heightBias: 14, heightScale: 1.4,
});
biome('jungle', {
  temperature: 0.95, downfall: 0.9,
  grassColor: 0x59c93c, foliageColor: 0x30bb0b, waterColor: 0x3f76e4,
  skyColor: 0x77a8ff, fogColor: 0xb8d8c0,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 22, treeKinds: [{ kind: 'jungle', weight: 7 }, { kind: 'big_jungle', weight: 3 }],
  grass: 24, flowers: 2, melon: 1, mushroom: 1,
  heightBias: 2, heightScale: 0.9,
  passiveMobs: [CHICKEN, PIG],
});
biome('swamp', {
  temperature: 0.8, downfall: 0.9,
  grassColor: 0x6a7039, foliageColor: 0x6a7039, waterColor: 0x617b64,
  skyColor: 0x78a7ff, fogColor: 0xa0b090,
  surfaceTop: 'grass_block', surfaceFiller: 'dirt', underwater: 'mud',
  trees: 2.5, treeKinds: [{ kind: 'swamp_oak', weight: 1 }],
  grass: 10, flowers: 1, mushroom: 3, sugarCane: 3, lilyPad: 4,
  heightBias: -3, heightScale: 0.25,
  passiveMobs: [CHICKEN],
});
biome('mushroom_fields', {
  temperature: 0.9, downfall: 1.0,
  grassColor: 0x55c93f, foliageColor: 0x2bbb0f, waterColor: 0x3f76e4,
  skyColor: 0x78a7ff, fogColor: 0xd8c8d8,
  surfaceTop: 'mycelium', surfaceFiller: 'dirt', underwater: 'dirt',
  trees: 0, treeKinds: [], grass: 1, flowers: 0, mushroom: 20,
  heightBias: 4, heightScale: 0.7,
});

// --- nether ---------------------------------------------------------------
biome('nether_wastes', {
  temperature: 2.0, downfall: 0.0,
  grassColor: 0x8a5a3a, foliageColor: 0x8a5a3a, waterColor: 0x905957,
  skyColor: 0x330808, fogColor: 0x330808,
  surfaceTop: 'netherrack', surfaceFiller: 'netherrack', underwater: 'netherrack',
  stone: 'netherrack',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  dimension: 'nether', hostile: true,
});
biome('soul_sand_valley', {
  temperature: 2.0, downfall: 0.0,
  grassColor: 0x8a7a6a, foliageColor: 0x8a7a6a, waterColor: 0x905957,
  skyColor: 0x1b4745, fogColor: 0x1b4745,
  surfaceTop: 'soul_sand', surfaceFiller: 'soul_soil', underwater: 'soul_soil',
  stone: 'netherrack',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  dimension: 'nether', hostile: true,
});
biome('crimson_forest', {
  temperature: 2.0, downfall: 0.0,
  grassColor: 0x9a3040, foliageColor: 0x9a3040, waterColor: 0x905957,
  skyColor: 0x330303, fogColor: 0x330303,
  surfaceTop: 'netherrack', surfaceFiller: 'netherrack', underwater: 'netherrack',
  stone: 'netherrack',
  trees: 6, treeKinds: [{ kind: 'mushroom', weight: 1 }], grass: 0, flowers: 0,
  dimension: 'nether', hostile: true,
});
biome('warped_forest', {
  temperature: 2.0, downfall: 0.0,
  grassColor: 0x2b6b62, foliageColor: 0x2b6b62, waterColor: 0x905957,
  skyColor: 0x1a051a, fogColor: 0x1a051a,
  surfaceTop: 'netherrack', surfaceFiller: 'netherrack', underwater: 'netherrack',
  stone: 'netherrack',
  trees: 6, treeKinds: [{ kind: 'mushroom', weight: 1 }], grass: 0, flowers: 0,
  dimension: 'nether', hostile: true,
});
biome('basalt_deltas', {
  temperature: 2.0, downfall: 0.0,
  grassColor: 0x6a6a6a, foliageColor: 0x6a6a6a, waterColor: 0x905957,
  skyColor: 0x685f70, fogColor: 0x685f70,
  surfaceTop: 'basalt', surfaceFiller: 'blackstone', underwater: 'blackstone',
  stone: 'blackstone',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  dimension: 'nether', hostile: true,
});

// --- the end --------------------------------------------------------------
biome('the_end', {
  temperature: 0.5, downfall: 0.5,
  grassColor: 0x8a8a8a, foliageColor: 0x8a8a8a, waterColor: 0x3f76e4,
  skyColor: 0x000000, fogColor: 0x0a0510,
  surfaceTop: 'end_stone', surfaceFiller: 'end_stone', underwater: 'end_stone',
  stone: 'end_stone',
  trees: 0, treeKinds: [], grass: 0, flowers: 0,
  dimension: 'the_end', hostile: true,
});
biome('end_highlands', {
  temperature: 0.5, downfall: 0.5,
  grassColor: 0x8a8a8a, foliageColor: 0x8a8a8a, waterColor: 0x3f76e4,
  skyColor: 0x000000, fogColor: 0x0a0510,
  surfaceTop: 'end_stone', surfaceFiller: 'end_stone', underwater: 'end_stone',
  stone: 'end_stone',
  trees: 2, treeKinds: [], grass: 0, flowers: 0,
  dimension: 'the_end', hostile: true,
});

export const BIOMES: readonly BiomeDef[] = biomes;
export const BIOME_COUNT = biomes.length;

export function biomeDef(name: string): BiomeDef {
  const b = biomeByName.get(name);
  if (!b) throw new Error(`unknown biome ${name}`);
  return b;
}
export function biomeById(id: number): BiomeDef {
  return biomes[id] ?? biomes[0]!;
}
export function biomeId(name: string): number {
  return biomeDef(name).id;
}

/**
 * Pick an overworld biome from climate parameters.
 *
 * `continentalness` runs from -1 (deep ocean) through 0 (coast) to 1 (inland),
 * `erosion` from -1 (mountainous) to 1 (flat), and temperature/humidity are
 * 0..1. `weirdness` breaks ties so that biome borders are not straight lines.
 */
export function selectBiome(
  continentalness: number,
  erosion: number,
  temperature: number,
  humidity: number,
  weirdness: number,
  isRiver: boolean,
): number {
  // Oceans first.
  if (continentalness < -0.45) {
    return temperature < 0.15 ? FROZEN_OCEAN : continentalness < -0.7 ? DEEP_OCEAN : OCEAN;
  }
  if (continentalness < -0.11) {
    return temperature < 0.15 ? FROZEN_OCEAN : OCEAN;
  }
  if (isRiver && erosion > -0.4) return RIVER;
  if (continentalness < -0.03) {
    // Coastal strip: sandy beach in warm climates, stony where it is eroded.
    if (erosion < -0.5) return STONY_SHORE;
    return temperature < 0.15 ? SNOWY_PLAINS : BEACH;
  }

  // Mountains dominate strongly negative erosion regardless of climate.
  if (erosion < -0.72) return JAGGED_PEAKS;
  if (erosion < -0.45) {
    if (temperature < 0.2) return SNOWY_TAIGA;
    return weirdness > 0.1 ? WINDSWEPT_HILLS : MEADOW;
  }

  // Mushroom fields are a rare oddity on isolated, very flat landmasses.
  if (continentalness > 0.7 && weirdness < -0.72 && humidity > 0.66) return MUSHROOM_FIELDS;

  if (temperature < 0.12) return humidity > 0.5 ? SNOWY_TAIGA : SNOWY_PLAINS;
  if (temperature < 0.32) return humidity > 0.45 ? TAIGA : SNOWY_PLAINS;
  if (temperature < 0.62) {
    if (humidity < 0.25) return PLAINS;
    if (humidity < 0.55) return weirdness > 0 ? FOREST : PLAINS;
    return weirdness > 0.3 ? BIRCH_FOREST : FOREST;
  }
  if (temperature < 0.85) {
    if (humidity < 0.22) return weirdness > 0.2 ? SAVANNA : PLAINS;
    if (humidity < 0.5) return weirdness > 0.4 ? SUNFLOWER_PLAINS : FOREST;
    if (humidity < 0.78) return weirdness < -0.3 ? DARK_FOREST : FOREST;
    return SWAMP;
  }
  // Hot.
  if (humidity < 0.3) return weirdness > 0.55 ? BADLANDS : DESERT;
  if (humidity < 0.5) return SAVANNA;
  if (humidity < 0.72) return weirdness > 0 ? JUNGLE : SAVANNA;
  return JUNGLE;
}

export const OCEAN = biomeId('ocean');
export const DEEP_OCEAN = biomeId('deep_ocean');
export const FROZEN_OCEAN = biomeId('frozen_ocean');
export const RIVER = biomeId('river');
export const BEACH = biomeId('beach');
export const STONY_SHORE = biomeId('stony_shore');
export const PLAINS = biomeId('plains');
export const SUNFLOWER_PLAINS = biomeId('sunflower_plains');
export const MEADOW = biomeId('meadow');
export const FOREST = biomeId('forest');
export const BIRCH_FOREST = biomeId('birch_forest');
export const DARK_FOREST = biomeId('dark_forest');
export const TAIGA = biomeId('taiga');
export const SNOWY_TAIGA = biomeId('snowy_taiga');
export const SNOWY_PLAINS = biomeId('snowy_plains');
export const WINDSWEPT_HILLS = biomeId('windswept_hills');
export const JAGGED_PEAKS = biomeId('jagged_peaks');
export const SAVANNA = biomeId('savanna');
export const DESERT = biomeId('desert');
export const BADLANDS = biomeId('badlands');
export const JUNGLE = biomeId('jungle');
export const SWAMP = biomeId('swamp');
export const MUSHROOM_FIELDS = biomeId('mushroom_fields');
export const NETHER_WASTES = biomeId('nether_wastes');
export const SOUL_SAND_VALLEY = biomeId('soul_sand_valley');
export const CRIMSON_FOREST = biomeId('crimson_forest');
export const WARPED_FOREST = biomeId('warped_forest');
export const BASALT_DELTAS = biomeId('basalt_deltas');
export const THE_END = biomeId('the_end');
export const END_HIGHLANDS = biomeId('end_highlands');
